import { NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ThirdPartySharedModule } from './third-party-shared.module';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { FireWorkConfettiComponent } from './../widgets/firework-confetti/firework-confetti.component';
import { ProtocolRequiredDirective } from './../directive/protocol-required.directive';
import { WholeCentsDirective } from './../directive/whole-cents.directive';
import { TrimInputDirective } from './../directive/trim-input.directive';
import { EditableSelectFocusFixDirective } from './../directive/editable-select-focus-fix.directive';
import { UsernamePipe } from './../pipes/username.pipe';
import { AccessControlDirective } from './../directive/access-control.directive';
import { ProfileResultsGridComponent } from './../../profiles/profile-results-grid/profile-results-grid.component';
import { TabsComponent, TabComponent, TabViewComponent, TabWellContentViewDirective, LazyTabContentDirective } from '../widgets/tabs';
import { PageSizeDropdownComponent } from '../widgets/page-size-dropdown/page-size-dropdown.component';
import { WlWysiwygEditorComponent } from '../widgets/wysiwyg-editor';
import { WlBusyOverlayComponent } from '../widgets/busy-overlay/busy-overlay.component';
import { ProfileTypeIconComponent } from './../../profiles/profile-type-icon/profile-type-icon.component';
import { ProfileTypeIconReportNameComponent } from 'src/app/profiles/profile-type-icon-report-name/profile-type-icon-report-name.component';
import { ProfileSearchAndSelectComponent } from './../component/profile-search-and-select/profile-search-and-select.component';
import { ProfileQuicksearchComponent } from './../../profiles/profile-quicksearch/profile-quicksearch.component';
import { DateSelectorComponent } from './../../config/date-selector/date-selector.component';
import { StateSelectorComponent } from '../../config/state-selector/state-selector.component';
import { StateSelectorModernComponent } from './../component/state-selector-modern/state-selector-modern.component';
import { ImageLookupComponent } from 'src/app/config/image-lookup/image-lookup.component';
import { ProfileCustomFieldComponent } from 'src/app/profiles/shared/profile-custom-field/profile-custom-field.component';
import { CreditCardInputComponent } from 'src/app/revenue/credit-card-input/credit-card-input.component';
import { CardConnectInputComponent } from '../../integrations/card-connect/card-connect-input/card-connect-input.component';
import { CardComponent } from '../component/card/card.component';
import { HeaderContentComponent, FooterContentComponent } from '../component/content-components';
import { AtlasCurrencyPipe } from '../pipes/atlas-currency.pipe';
import { ShareLinkDialogComponent } from '../component/share-link-dialog/share-link-dialog.component';
import { EphemeralContentSwapComponent } from '../component/ephemeral-content-swap/ephemeral-content-swap.component';
import { AffCodeChipSelectorComponent } from 'src/app/affcode/affcode-chip-selector/affcode-chip-selector.component';
import { ModularWlWysiwygEditorComponent } from '../widgets/modular-wysiwyg-editor/modular-wysiwyg-editor.component';
import { ProfileAvatarComponent } from 'src/app/profiles/profile-avatar/profile-avatar.component';
import { AuthInterceptor } from '../http/interceptors/auth-interceptor';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';
import { AffCodeSelectorComponent } from 'src/app/affcode/affcode-selector/affcode-selector.component';
import { WlHasBetaFeatureDirective } from '../directive/wlHasBetaFeature.directive';
import { AchInputComponent } from 'src/app/revenue/ach-input/ach-input.component';
import { HtmlContentViewComponent } from '../component/html-content-view/html-content-view.component';
import { AdjustmentTypeSelectorComponent } from 'src/app/revenue/adjustment-type-selector/adjustment-type-selector.component';
import { AffCodeMassAddComponent } from 'src/app/affcode/affcode-mass-add/affcode-mass-add.component';
import { PaymentTypeSelectorComponent } from 'src/app/revenue/payment-type-selector/payment-type-selector.component';
import { ProfileSelectorComponent } from 'src/app/profiles/profile-selector/profile-selector.component';
import { TimeZoneSelectorComponent } from 'src/app/config/time-zone-selector/time-zone-selector.component';
import { TimeSelectorComponent } from 'src/app/config/time-selector/time-selector.component';
import { UserSelectorComponent } from 'src/app/users/user-selector/user-selector.component';
import { RevenueItemSelectorComponent } from 'src/app/revenue/revenue-item-selector/revenue-item-selector.component';
import { RelatedProfileSelectorComponent } from 'src/app/profiles/related-profile-selector/related-profile-selector.component';
import { ProfileDupCheckerComponent } from 'src/app/profiles/profile-dup-checker/profile-dup-checker.component';
import { WlCeuTypeDropdownComponent } from 'src/app/config/ceu/ceu-type-dropdown/ceu-type-dropdown.component';
import { SimpleHtmlEditorComponent } from '../widgets/html-editor/simple-html-editor/simple-html-editor.component';
import { BusyIndicatorComponent } from '../widgets/busy-indicator/busy-indicator.component';
import { EntityDomainEditDialogComponent } from 'src/app/admin/global-settings/entity-domain-management/entity-domain-edit-dialog/entity-domain-edit-dialog.component';
import { PaymentSourceEditDialogComponent } from 'src/app/admin/codes/revenue-codes/payment-source-management/payment-source-edit-dialog/payment-source-edit-dialog.component';
import { EmailInputComponent } from '../widgets/email-input/email-input.component';
import { BackToTopComponent } from 'src/app/shared/component/back-to-top/back-to-top.component';
import { SaveCancelActionsComponent } from 'src/app/shared/component/save-cancel-actions/save-cancel-actions.component';
import { AddButtonComponent } from '../component/buttons/add-button.component';
import { ActionButtonComponent } from '../component/buttons/action-button.component';
import { CancelButtonComponent } from '../component/buttons/cancel-button.component';

import { WlDayDifferenceCountPipe } from './../pipes/day-difference-count.pipe';
import { WlRegexReplacePipe } from './../pipes/regex-replace.pipe';
import { WlUpperCaseFirstLetterPipe } from './../pipes/ucfirst.pipe';
import { WlTimeTranslatePipe } from './../pipes/time-translate.pipe';
import { PercentPipe } from './../pipes/percent.pipe';
import { NotificationBannerComponent } from '../component/notification-banner/notification-banner.component';

const declarations = [
  LazyTabContentDirective,
  AccessControlDirective,
  ProtocolRequiredDirective,
  WholeCentsDirective,
  TrimInputDirective,
  EditableSelectFocusFixDirective,
  WlHasBetaFeatureDirective,
  PageSizeDropdownComponent,
  WlWysiwygEditorComponent,
  EmailInputComponent,
  WlBusyOverlayComponent,
  ProfileTypeIconComponent,
  ProfileTypeIconReportNameComponent,
  ProfileSearchAndSelectComponent,
  ProfileQuicksearchComponent,
  ProfileResultsGridComponent,
  DateSelectorComponent,
  StateSelectorComponent,
  StateSelectorModernComponent,
  ImageLookupComponent,
  ProfileCustomFieldComponent,
  CreditCardInputComponent,
  CardConnectInputComponent,
  HeaderContentComponent,
  FooterContentComponent,
  CardComponent,
  FireWorkConfettiComponent,
  ShareLinkDialogComponent,
  EphemeralContentSwapComponent,
  AffCodeChipSelectorComponent,
  ModularWlWysiwygEditorComponent,
  SimpleHtmlEditorComponent,
  ProfileAvatarComponent,
  AffCodeSelectorComponent,
  AchInputComponent,
  HtmlContentViewComponent,
  AdjustmentTypeSelectorComponent,
  AffCodeMassAddComponent,
  PaymentTypeSelectorComponent,
  ProfileSelectorComponent,
  RelatedProfileSelectorComponent,
  ProfileDupCheckerComponent,
  TimeZoneSelectorComponent,
  TimeSelectorComponent,
  UserSelectorComponent,
  RevenueItemSelectorComponent,
  WlCeuTypeDropdownComponent,
  BusyIndicatorComponent,
  EntityDomainEditDialogComponent,
  PaymentSourceEditDialogComponent,
  BackToTopComponent,
  SaveCancelActionsComponent,
  AddButtonComponent,
  ActionButtonComponent,
  CancelButtonComponent,
  NotificationBannerComponent,
];

const pipes = [WlUpperCaseFirstLetterPipe, SafeHtmlPipe, AtlasCurrencyPipe, UsernamePipe, WlDayDifferenceCountPipe];

//const standaloneComponents = [TabsComponent,  TabComponent, TabViewComponent];
const standalonePipes = [WlTimeTranslatePipe, PercentPipe, WlRegexReplacePipe];

const sharedImports = [ThirdPartySharedModule];

@NgModule({
  declarations: [...declarations, ...pipes],
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ThirdPartySharedModule,
    HttpClientModule,
    ...standalonePipes,
    TabsComponent,
    TabComponent,
    TabViewComponent,
    TabWellContentViewDirective
  ],
  exports: [
    ...declarations,
    ...sharedImports,
    CommonModule,
    FormsModule,
    TranslateModule,
    ...pipes,
    ...standalonePipes,
    TabsComponent,
    TabComponent,
    TabViewComponent,
  ],
  providers: [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }, WlUpperCaseFirstLetterPipe],
})
export class AtlasSharedModule {}
